<script setup lang="ts">
import { ref, provide, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useCopilotWidget } from '@/composables/useCopilotWidget'
import { useCopilotStore } from '@/views/Copilot/store'
import { useProjectsStore } from '@/views/Projects/store'
import { useCopilotChat } from '@/views/Copilot/composables/useCopilotChat'
import ContextPanel from '@/views/Copilot/components/ContextPanel.vue'
import CopilotChatPanel from '@/views/Copilot/components/CopilotChatPanel.vue'
import OutputPanel from '@/views/Copilot/components/OutputPanel.vue'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { BotMessageSquare, RotateCcw, Minus, X } from 'lucide-vue-next'

const { isOpen, minimize, close } = useCopilotWidget()

const copilotStore = useCopilotStore()
const projectsStore = useProjectsStore()
const { projectContext } = storeToRefs(copilotStore)

// Single shared chat instance
const copilotChat = useCopilotChat()
provide('copilotChat', copilotChat)

const activeTab = ref('chat')

onMounted(async () => {
  if (projectsStore.projects.length === 0) {
    await projectsStore.fetchProjects()
  }
})

const handleNewSession = () => {
  copilotChat.closeSession()
  copilotStore.clearContext()
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-[100] bg-background flex flex-col">
        <!-- Header -->
        <div class="h-14 border-b px-6 py-3 flex items-center justify-between shrink-0">
          <div class="flex items-center gap-2">
            <BotMessageSquare class="h-5 w-5" />
            <h1 class="text-lg font-semibold">Copilot</h1>
            <Badge v-if="projectContext" variant="outline">
              {{ projectContext.projectName }}
            </Badge>
          </div>
          <div class="flex items-center gap-2">
            <Button variant="outline" size="sm" @click="handleNewSession">
              <RotateCcw class="h-3.5 w-3.5 mr-1.5" />
              Nueva Sesion
            </Button>
            <Button variant="ghost" size="icon" title="Minimizar" @click="minimize">
              <Minus class="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Cerrar" @click="close">
              <X class="h-4 w-4" />
            </Button>
          </div>
        </div>

        <!-- Desktop: 3-panel layout -->
        <div class="flex-1 min-h-0 hidden lg:flex">
          <ContextPanel class="w-[320px] shrink-0 border-r" />
          <CopilotChatPanel class="flex-1" />
          <OutputPanel class="w-[400px] shrink-0 border-l" />
        </div>

        <!-- Mobile: Tabs layout -->
        <div class="flex-1 min-h-0 lg:hidden flex flex-col">
          <Tabs v-model="activeTab" class="flex-1 flex flex-col">
            <TabsList class="w-full grid grid-cols-3 shrink-0 rounded-none border-b">
              <TabsTrigger value="context">Contexto</TabsTrigger>
              <TabsTrigger value="chat">Chat</TabsTrigger>
              <TabsTrigger value="output">Output</TabsTrigger>
            </TabsList>
            <TabsContent value="context" class="flex-1 m-0 min-h-0">
              <ContextPanel class="h-full" />
            </TabsContent>
            <TabsContent value="chat" class="flex-1 m-0 min-h-0">
              <CopilotChatPanel />
            </TabsContent>
            <TabsContent value="output" class="flex-1 m-0 min-h-0">
              <OutputPanel />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
